// ============================================================
// Game Balance Constants - Idle RPG Auto-Battler v2.0
// ============================================================
// ---------- Hero ----------
/** XP required for level N = BASE * (N ^ EXPONENT).
 *  Base bumped 100 → 160 (+60 %) to slow hero progression — every hero level
 *  now demands 60 % more XP than before, uniformly across the whole curve. */
export const XP_BASE = 160;
export const XP_EXPONENT = 1.5;
/** Base stats per class at level 1 (v2.0 overhaul) */
// Melee class durability buff: warrior / tank / dwarf / valkyrie / assassin
// each get +15 % HP and +20 % DEF baseline (and matching growth) so they
// can stay in melee range without being shredded. The defense passive
// tree was nerfed in the same pass to leave headroom for these class-
// level buffs without double-stacking durability.
export const CLASS_BASE_STATS = {
    warrior: { hp: 291, maxHp: 291, mp: 0, maxMp: 0, attack: 31, defense: 24, speed: 1.0, critChance: 0.10, critDamage: 1.5, spellPower: 0 },
    tank: { hp: 469, maxHp: 469, mp: 80, maxMp: 80, attack: 24, defense: 38, speed: 0.7, critChance: 0.03, critDamage: 1.3, spellPower: 5 },
    wizard: { hp: 165, maxHp: 165, mp: 180, maxMp: 180, attack: 30, defense: 7, speed: 0.9, critChance: 0.08, critDamage: 1.8, spellPower: 30 },
    archer: { hp: 198, maxHp: 198, mp: 60, maxMp: 60, attack: 30, defense: 13, speed: 1.3, critChance: 0.12, critDamage: 1.6, spellPower: 0 },
    healer: { hp: 154, maxHp: 154, mp: 140, maxMp: 140, attack: 21, defense: 8, speed: 0.8, critChance: 0.04, critDamage: 1.4, spellPower: 25 },
    assassin: { hp: 209, maxHp: 209, mp: 0, maxMp: 0, attack: 32, defense: 12, speed: 1.5, critChance: 0.15, critDamage: 2.0, spellPower: 0 },
    druid: { hp: 193, maxHp: 193, mp: 150, maxMp: 150, attack: 26, defense: 11, speed: 0.95, critChance: 0.07, critDamage: 1.7, spellPower: 24 },
    shaman: { hp: 171, maxHp: 171, mp: 160, maxMp: 160, attack: 22, defense: 9, speed: 0.90, critChance: 0.06, critDamage: 1.7, spellPower: 26 },
    dwarf: { hp: 392, maxHp: 392, mp: 0, maxMp: 0, attack: 36, defense: 34, speed: 0.75, critChance: 0.06, critDamage: 1.9, spellPower: 0 },
    amazon: { hp: 215, maxHp: 215, mp: 80, maxMp: 80, attack: 32, defense: 14, speed: 1.1, critChance: 0.14, critDamage: 1.8, spellPower: 0 },
    // Valkyrie (Valka) — warrior-class heroKey override. Sits between warrior (230 HP)
    // and dwarf (310 HP): tanky-DPS, frontline barbarian with shield + banner support.
    // Higher HP + DEF than baseline warrior, lower ATK than Lootfi. Uses rage.
    valkyrie: { hp: 380, maxHp: 380, mp: 0, maxMp: 0, attack: 30, defense: 29, speed: 0.9, critChance: 0.08, critDamage: 1.6, spellPower: 0 },
    necromancer: { hp: 171, maxHp: 171, mp: 160, maxMp: 160, attack: 27, defense: 8, speed: 0.85, critChance: 0.06, critDamage: 1.7, spellPower: 28 },
    // Cyrith the Stormbound (Sorcerer) — wizard heroKey override. Battlemage
    // hybrid: 2H sword + offhand staff, summons Copper Golem. ATK + SP both
    // scale his kit so neither stat alone is optimal. HP between warrior and
    // wizard (mail caster), defense slightly above archer, SP below pure wizard.
    sorcerer: { hp: 210, maxHp: 210, mp: 140, maxMp: 140, attack: 27, defense: 14, speed: 1.0, critChance: 0.10, critDamage: 1.7, spellPower: 22 },
};
/** Stat growth per level per class (added to base each level) */
export const CLASS_STAT_GROWTH = {
    warrior: { hp: 29, mp: 0, attack: 2.8, defense: 2.4, speed: 0, critChance: 0.002, critDamage: 0.01, spellPower: 0 },
    tank: { hp: 55, mp: 4, attack: 1.5, defense: 4.2, speed: 0, critChance: 0.001, critDamage: 0.005, spellPower: 1 },
    wizard: { hp: 14, mp: 15, attack: 2, defense: 0.8, speed: 0, critChance: 0.003, critDamage: 0.015, spellPower: 5 },
    archer: { hp: 20, mp: 4, attack: 3.5, defense: 1.8, speed: 0.01, critChance: 0.004, critDamage: 0.012, spellPower: 0 },
    healer: { hp: 14, mp: 12, attack: 1.5, defense: 1.2, speed: 0, critChance: 0.001, critDamage: 0.005, spellPower: 4 },
    assassin: { hp: 21, mp: 0, attack: 4.2, defense: 1.8, speed: 0.01, critChance: 0.005, critDamage: 0.02, spellPower: 0 },
    druid: { hp: 19, mp: 12, attack: 2.4, defense: 1.5, speed: 0, critChance: 0.003, critDamage: 0.013, spellPower: 4 },
    shaman: { hp: 17, mp: 13, attack: 2.0, defense: 1.2, speed: 0, critChance: 0.003, critDamage: 0.013, spellPower: 4 },
    dwarf: { hp: 37, mp: 0, attack: 3.5, defense: 3.84, speed: 0, critChance: 0.001, critDamage: 0.015, spellPower: 0 },
    amazon: { hp: 22, mp: 5, attack: 3.5, defense: 1.8, speed: 0.008, critChance: 0.004, critDamage: 0.014, spellPower: 0 },
    // Valkyrie — mid HP/DEF growth (between warrior +25 and dwarf +32), solid ATK
    // scaling so late-game she still contributes damage pressure.
    valkyrie: { hp: 39, mp: 0, attack: 2.6, defense: 2.88, speed: 0, critChance: 0.002, critDamage: 0.012, spellPower: 0 },
    necromancer: { hp: 15, mp: 14, attack: 1.8, defense: 0.8, speed: 0, critChance: 0.002, critDamage: 0.014, spellPower: 5 },
    // Sorcerer (Cyrith) — middle HP growth (between warrior 25 and wizard 14),
    // hybrid ATK + SP scaling per level so neither stat outpaces the other.
    sorcerer: { hp: 22, mp: 10, attack: 2.5, defense: 1.5, speed: 0, critChance: 0.003, critDamage: 0.013, spellPower: 4 },
};
/** Hero respawn time in ms — flat 3 seconds, 1 free use per game per hero */
export const RESPAWN_TIME_BASE = 3000;
/** Mana regen per second (default for most classes — v6.0 increased for casters) */
export const MANA_REGEN_PER_SEC = 5;
/** Wizard mana regen per second (v6.0 increased) */
export const WIZARD_MANA_REGEN_PER_SEC = 4;
/** Shaman mana regen per second — highest of all classes; totems are mana-hungry */
export const SHAMAN_MANA_REGEN_PER_SEC = 6;
/** Assassin energy regen per second */
export const ASSASSIN_ENERGY_REGEN_PER_SEC = 10;
// ---------- Warrior ----------
/** Melee range in pixels — warrior deals full damage within this range */
export const WARRIOR_MELEE_RANGE = 160;
// ── Spell cast range tiers ──────────────────────────────────────────────
// Per-spell hard range. If a spell has a castRange in this map and there
// is no living enemy within that distance from the caster, the cast is
// skipped (mana not spent, CD doesn't tick). Spells NOT in this map have
// no range gate (buffs, heals, summons, global AoE).
//
// Tier values:
//   240 px — Melee     (sword swing + a small margin)
//   400 px — Mid       (≈ 1/3 of map; thrown weapons, charges, short bolts)
//   550 px — Extended  (chain lightning, AoE with reach)
//   750 px — Ranged    (standard projectile range; bows, casters)
//   (no entry) — Global / battlefield-wide AoE / non-targeted
export const SPELL_RANGE_MELEE = 240;
export const SPELL_RANGE_MID = 400;
export const SPELL_RANGE_EXTENDED = 550;
export const SPELL_RANGE_RANGED = 750;
export const SPELL_CAST_RANGES = {
    // Melee
    whirlwind: SPELL_RANGE_MELEE,
    earthquake: SPELL_RANGE_MELEE,
    sinister_strike: SPELL_RANGE_MELEE,
    whirlwind_bludgeon: SPELL_RANGE_MELEE, // Tempest Slam
    // Mid
    challenge: SPELL_RANGE_MID,
    thunder_strike: SPELL_RANGE_MID,
    mace_throw: SPELL_RANGE_MID,
    poison_cloud: SPELL_RANGE_MID,
    shield_bash_charge: SPELL_RANGE_MID,
    jump_strike: SPELL_RANGE_MID, // Leap Strike
    fear: SPELL_RANGE_MID,
    thornlash: SPELL_RANGE_MID,
    wild_growth: SPELL_RANGE_MID,
    shaman_totems: SPELL_RANGE_MID,
    static_field: SPELL_RANGE_MID,
    // Extended mid
    vine_snare: SPELL_RANGE_EXTENDED,
    arc_strike: SPELL_RANGE_EXTENDED,
    // Ranged
    fireball: SPELL_RANGE_RANGED,
    multishot: SPELL_RANGE_RANGED,
    piercing_shot: SPELL_RANGE_RANGED,
    word_of_death: SPELL_RANGE_RANGED,
    shadow_step: SPELL_RANGE_RANGED, // Wraith Strike — teleports
    freezing_shot: SPELL_RANGE_RANGED,
    shuriken_barrage: SPELL_RANGE_RANGED,
    death_coil: SPELL_RANGE_RANGED,
    storm_bolt: SPELL_RANGE_RANGED,
    // Global / no entry: consecration, eruption, buffs, heals, summons
};
/** Damage multiplier beyond melee range */
export const WARRIOR_RANGE_DAMAGE_MULT = 0.40;
/** Damage multiplier when rage is full */
export const WARRIOR_FULL_RAGE_MULT = 1.5;
/** Rage gained per auto-attack hit */
export const WARRIOR_RAGE_PER_HIT = 5;
/** Rage gained per crit (double normal) */
export const WARRIOR_RAGE_PER_CRIT = 10;
/** Rage gained when taking a hit */
export const WARRIOR_RAGE_ON_DAMAGED = 5;
// ---------- Tank (Ironheld) ----------
/** Tank deals reduced damage at range (ranged auto-attack like warrior, less damage) */
export const TANK_RANGE_DAMAGE_MULT = 0.35;
/** Ironheld stops advancing and stands to fight when the nearest enemy is within this range */
export const TANK_AUTO_ATTACK_RANGE = 220;
// ---------- Archer ----------
/** Max arrows before reload is required */
export const ARCHER_MAX_ARROWS = 10;
/** Reload duration in ms */
export const ARCHER_RELOAD_TIME = 1000;
// ---------- Wizard ----------
/** Frostbolt slow fraction (0.5 = 50% speed reduction) */
export const WIZARD_FROSTBALL_SLOW = 0.5;
/** Frostbolt slow duration in ms */
export const WIZARD_FROSTBALL_SLOW_DURATION = 3000;
// ---------- Assassin ----------
/** Max energy for assassin */
export const ASSASSIN_MAX_ENERGY = 100;
// ---------- Enemies ----------
/** Enemy HP multiplier per zone level (stages 11+). Stages 1-10 use the softer early
 *  multiplier below so new players don't hit a wall before they've learned the kit. */
export const ENEMY_HP_PER_LEVEL = 1.15;
/** Early-game enemy HP multiplier — applied compounding for stages 1 through
 *  ENEMY_HP_EARLY_CAP (inclusive). Past the cap, the late-game multiplier takes over
 *  using the stage-cap HP as its new baseline. */
export const ENEMY_HP_PER_LEVEL_EARLY = 1.10;
export const ENEMY_HP_EARLY_CAP = 10;
/** Enemy attack multiplier per zone level */
export const ENEMY_ATK_PER_LEVEL = 1.12;
/** Enemy defense multiplier per zone level */
export const ENEMY_DEF_PER_LEVEL = 1.10;
/** Enemy gold reward multiplier per zone level */
export const ENEMY_GOLD_PER_LEVEL = 1.08;
/** Enemy XP reward multiplier per zone level */
export const ENEMY_XP_PER_LEVEL = 1.10;
/** Elite multipliers (on top of zone scaling).
 *  HP was 2× through most of the game's life; bumped to 3× (2 × 1.5) to close the
 *  "elites feel like chunky minions, not a real threat" gap — especially bad on
 *  stage 40+ where the boss HP curve had pulled away from elite HP entirely.
 *  ATK mult introduced alongside so elites hit harder too, not just tankier. */
export const ELITE_HP_MULT = 3;
export const ELITE_ATK_MULT = 1.5;
/** Boss multipliers (on top of zone scaling). Boss HP trimmed 10 % (8 → 7.2)
 *  to narrow the elite-vs-boss HP gap at high stages — some bosses at lv 50+
 *  read as "near-infinite HP" next to even the buffed elites. */
export const BOSS_HP_MULT = 7.2;
export const BOSS_ATK_MULT = 2;
export const BOSS_DEF_MULT = 2;
export const BOSS_GOLD_MULT = 20;
export const BOSS_XP_MULT = 15;
export const BOSS_LOOT_CHANCE = 1.0; // Guaranteed drop
/** Levels per zone (e.g. levels 1-2 = Dark Forest, 3-4 = Haunted Crypts, etc.) */
export const LEVELS_PER_ZONE = 2;
/** Waves per level before boss (5 waves × 2 levels = 10 waves per biome) */
export const WAVES_PER_ZONE = 5;
/** Enemies per wave range at zone level 1 */
export const MIN_ENEMIES_WAVE = 3;
export const MAX_ENEMIES_WAVE = 6;
/** Spawn delay between enemies in ms */
export const ENEMY_SPAWN_DELAY = 800;
/**
 * Enemy speed scaling by zone level:
 * Levels 1-10: movespeed reduced 66%, attack speed reduced 33%
 * Levels 11-99: linearly scale up to 100% at level 100
 * Level 100+: +1% per level above 100
 */
export const EARLY_GAME_MOVE_SPEED_PENALTY = 0.34; // 34% of normal (66% reduction)
export const EARLY_GAME_ATK_SPEED_PENALTY = 0.67; // 67% of normal (33% reduction)
export const EARLY_GAME_MAX_LEVEL = 10;
export const MAX_SPEED_LEVEL = 100;
// ---------- Base ----------
export const BASE_STARTING_HP = 500;
export const BASE_HP_REGEN = 1;
export const BASE_STARTING_HERO_SLOTS = 1;
export const BASE_MAX_HERO_SLOTS = 6;
/** Starting base arrow attack damage */
export const BASE_STARTING_ATTACK = 5;
/** Starting base attack speed (ms between arrows) */
export const BASE_STARTING_ATTACK_SPEED = 3000;
// ---------- Loot ----------
/** Global multiplier applied to all item drop chances (reduced 50%; 0.25 of raw rate) */
export const LOOT_DROP_GLOBAL_MULT = 0.25;
/** Drop rate multipliers by rarity */
export const RARITY_DROP_WEIGHTS = {
    common: 60,
    uncommon: 25,
    rare: 10,
    epic: 4,
    legendary: 1,
    unique: 0.15, // rolled directly for relics only; rare but obtainable within a few runs of grinding
};
/** Stat bonus multiplier by rarity */
export const RARITY_STAT_MULT = {
    common: 1.0,
    uncommon: 1.3,
    rare: 1.6,
    epic: 2.0,
    legendary: 3.0,
    unique: 0, // unique relics have no random stats
};
/** Materials gained from recycling items by rarity */
export const RARITY_RECYCLE_MATS = {
    common: 1,
    uncommon: 3,
    rare: 8,
    epic: 20,
    legendary: 50,
    unique: 0,
};
/** Equipment sell value multiplier by rarity */
export const RARITY_SELL_MULT = {
    common: 1,
    uncommon: 3,
    rare: 8,
    epic: 25,
    legendary: 100,
    unique: 200,
};
/** Rarity colors for UI display */
export const RARITY_COLORS = {
    common: '#9ca3af',
    uncommon: '#22c55e',
    rare: '#3b82f6',
    epic: '#a855f7', // violet/purple
    legendary: '#f97316',
    unique: '#cc3322', // red — unique relics
};
// ---------- Base Upgrade Costs (exponential OGame-style) ----------
/** Cost multiplier per upgrade level */
export const UPGRADE_COST_EXPONENT = 1.8;
/** Base costs per upgrade category (x3 for v3.0 balance) */
export const UPGRADE_BASE_COSTS = {
    fortification: { gold: 300, loot: 60 },
    barracks: { gold: 600, loot: 150 },
    armory: { gold: 450, loot: 90 },
    resource: { gold: 240, loot: 45 },
    research: { gold: 750, loot: 180 },
};
// ---------- Merchant ----------
/** Merchant inventory refreshes every N waves */
export const MERCHANT_REFRESH_INTERVAL = 10;
/** Number of items in merchant inventory */
export const MERCHANT_INVENTORY_SIZE = 4;
// ---------- Auto-Battle Timing ----------
/** Milliseconds between each auto-attack tick */
export const BATTLE_TICK_MS = 100;
/** Base attack interval = 1000 / speed stat */
export const BASE_ATTACK_INTERVAL = 1000;
//# sourceMappingURL=balance.js.map